import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { 
  Building2, 
  Plus, 
  ExternalLink, 
  Settings, 
  Trash2, 
  Eye,
  MessageSquare,
  LayoutDashboard,
  Check,
  X,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { tenants } from '../config/tenants';

// XO Tenant Management Panel - Onboard and manage clients

export function TenantManagement() {
  const [clientList, setClientList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    agentName: '',
    website: '',
    primaryColor: '#FF0055'
  });

  useEffect(() => {
    // Load tenants from config
    const clients = Object.values(tenants).map(t => ({
      ...t,
      status: 'active',
      conversations: Math.floor(Math.random() * 500) + 100,
      satisfaction: Math.floor(Math.random() * 15) + 85
    }));
    setClientList(clients);
  }, []);

  const handleAddClient = () => {
    if (!newClient.name || !newClient.agentName) return;
    
    const id = newClient.name.toLowerCase().replace(/\s+/g, '-');
    const client = {
      id,
      name: newClient.name,
      agentName: newClient.agentName,
      tagline: 'AI Assistant',
      logo: `https://ui-avatars.com/api/?name=${encodeURIComponent(newClient.name)}&background=random&size=128`,
      website: newClient.website,
      colors: {
        primary: newClient.primaryColor,
        primaryHover: newClient.primaryColor + 'CC',
      },
      status: 'pending',
      conversations: 0,
      satisfaction: 0
    };
    
    setClientList(prev => [...prev, client]);
    setNewClient({ name: '', agentName: '', website: '', primaryColor: '#FF0055' });
    setDialogOpen(false);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="tenant-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-outfit font-bold text-2xl sm:text-3xl text-white mb-2">
              Client Management
            </h1>
            <p className="text-[#A1A1AA]">
              Manage your white-label XOClaw deployments
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-bold" data-testid="add-client-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add New Client
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121214] border-[#27272A] text-white">
              <DialogHeader>
                <DialogTitle className="font-outfit text-xl">Onboard New Client</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-white">Company Name</Label>
                  <Input
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    placeholder="e.g., ESL Gaming"
                    className="bg-[#1A1A1E] border-[#27272A] text-white placeholder:text-[#A1A1AA]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Agent Name</Label>
                  <Input
                    value={newClient.agentName}
                    onChange={(e) => setNewClient({ ...newClient, agentName: e.target.value })}
                    placeholder="e.g., ESLClaw"
                    className="bg-[#1A1A1E] border-[#27272A] text-white placeholder:text-[#A1A1AA]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Website</Label>
                  <Input
                    value={newClient.website}
                    onChange={(e) => setNewClient({ ...newClient, website: e.target.value })}
                    placeholder="https://eslgaming.com"
                    className="bg-[#1A1A1E] border-[#27272A] text-white placeholder:text-[#A1A1AA]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Brand Color</Label>
                  <div className="flex gap-3">
                    <Input
                      type="color"
                      value={newClient.primaryColor}
                      onChange={(e) => setNewClient({ ...newClient, primaryColor: e.target.value })}
                      className="w-16 h-10 p-1 bg-[#1A1A1E] border-[#27272A]"
                    />
                    <Input
                      value={newClient.primaryColor}
                      onChange={(e) => setNewClient({ ...newClient, primaryColor: e.target.value })}
                      className="flex-1 bg-[#1A1A1E] border-[#27272A] text-white"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1 border-[#27272A] text-white hover:bg-[#1A1A1E]"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-bold"
                    onClick={handleAddClient}
                    disabled={!newClient.name || !newClient.agentName}
                  >
                    Create Client
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 rounded-lg bg-[#121214] border border-[#27272A]">
            <Building2 className="w-8 h-8 text-[#00F0FF] mb-2" />
            <p className="text-2xl font-bold text-white">{clientList.length}</p>
            <p className="text-sm text-[#A1A1AA]">Total Clients</p>
          </div>
          <div className="p-4 rounded-lg bg-[#121214] border border-[#27272A]">
            <Check className="w-8 h-8 text-green-400 mb-2" />
            <p className="text-2xl font-bold text-white">{clientList.filter(c => c.status === 'active').length}</p>
            <p className="text-sm text-[#A1A1AA]">Active</p>
          </div>
          <div className="p-4 rounded-lg bg-[#121214] border border-[#27272A]">
            <MessageSquare className="w-8 h-8 text-[#7000FF] mb-2" />
            <p className="text-2xl font-bold text-white">{clientList.reduce((acc, c) => acc + (c.conversations || 0), 0).toLocaleString()}</p>
            <p className="text-sm text-[#A1A1AA]">Total Conversations</p>
          </div>
          <div className="p-4 rounded-lg bg-[#121214] border border-[#27272A]">
            <Check className="w-8 h-8 text-[#FF0055] mb-2" />
            <p className="text-2xl font-bold text-white">
              {Math.round(clientList.reduce((acc, c) => acc + (c.satisfaction || 0), 0) / clientList.length)}%
            </p>
            <p className="text-sm text-[#A1A1AA]">Avg Satisfaction</p>
          </div>
        </div>

        {/* Client Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clientList.map((client, idx) => (
            <div 
              key={client.id}
              className="rounded-lg bg-[#121214] border border-[#27272A] overflow-hidden hover:border-[#00F0FF]/30 transition-colors"
            >
              {/* Header with brand color */}
              <div 
                className="h-2"
                style={{ background: client.colors?.primary || '#00F0FF' }}
              />
              
              <div className="p-4">
                {/* Client Info */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#1A1A1E] p-1">
                    <img 
                      src={client.logo}
                      alt={client.name}
                      className="w-full h-full object-contain rounded"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-white">{client.name}</h3>
                    <p className="text-sm text-[#A1A1AA]">{client.agentName}</p>
                  </div>
                  <span 
                    className={`text-xs px-2 py-1 rounded-full ${
                      client.status === 'active' 
                        ? 'bg-green-400/10 text-green-400' 
                        : 'bg-yellow-400/10 text-yellow-400'
                    }`}
                  >
                    {client.status}
                  </span>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-2 rounded bg-[#1A1A1E]">
                    <p className="text-xs text-[#A1A1AA]">Conversations</p>
                    <p className="font-bold text-white">{client.conversations?.toLocaleString() || 0}</p>
                  </div>
                  <div className="p-2 rounded bg-[#1A1A1E]">
                    <p className="text-xs text-[#A1A1AA]">Satisfaction</p>
                    <p className="font-bold text-green-400">{client.satisfaction || 0}%</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  {client.id === 'arena' ? (
                    <>
                      <Link to="/arena/login" className="flex-1">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full border-[#27272A] text-white hover:bg-[#1A1A1E]"
                          data-testid="arena-dashboard-btn"
                        >
                          <LayoutDashboard className="w-4 h-4 mr-1" />
                          Dashboard
                        </Button>
                      </Link>
                      <a href="/arena/chat" target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button 
                          size="sm" 
                          className="w-full"
                          style={{ background: client.colors?.primary || '#00F0FF', color: '#000' }}
                          data-testid="arena-player-chat-btn"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Player Chat
                        </Button>
                      </a>
                    </>
                  ) : (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1 border-[#27272A] text-white hover:bg-[#1A1A1E]"
                        disabled={client.status === 'pending'}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Configure
                      </Button>
                      <Button 
                        size="sm" 
                        className="flex-1"
                        style={{ background: client.colors?.primary || '#00F0FF', color: '#000' }}
                        disabled={client.status === 'pending'}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div className="rounded-lg bg-[#121214] border border-[#27272A] p-6">
          <h2 className="font-outfit font-bold text-lg text-white mb-4">How White-Label Works</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center mb-3">
                <span className="text-[#00F0FF] font-bold">1</span>
              </div>
              <h3 className="font-bold text-white mb-1">Onboard Client</h3>
              <p className="text-sm text-[#A1A1AA]">Add client details, logo, and brand colors. Configure their agent name.</p>
            </div>
            <div>
              <div className="w-10 h-10 rounded-lg bg-[#7000FF]/10 border border-[#7000FF]/30 flex items-center justify-center mb-3">
                <span className="text-[#7000FF] font-bold">2</span>
              </div>
              <h3 className="font-bold text-white mb-1">Connect Data</h3>
              <p className="text-sm text-[#A1A1AA]">Link their tournament API, Google Sheet, or upload data manually.</p>
            </div>
            <div>
              <div className="w-10 h-10 rounded-lg bg-[#FF0055]/10 border border-[#FF0055]/30 flex items-center justify-center mb-3">
                <span className="text-[#FF0055] font-bold">3</span>
              </div>
              <h3 className="font-bold text-white mb-1">Deploy Widget</h3>
              <p className="text-sm text-[#A1A1AA]">Client adds embed code to their website. AI agent goes live!</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
